import { SectionBackground } from '@/components/ui/section-background';
import { Button } from '@/components/ui/button';
import { LogOut, Camera, Pencil, Check, X } from 'lucide-react';
import { useProfile } from '@/lib/providers';
import { ActivityCalendar } from '@/components/profile/activity-calendar';
import { supabase } from '@/lib/supabase';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useContentPlays } from '@/lib/hooks/use-content-plays';
import { getMostRecentMondayUTC, splitNames } from '@/lib/utils';
import { getDeepLink, LOGIN_PATH } from '@/lib/constants';
import { TypesBarChart } from '@/components/charts/types-bar-chart';
import AudioCard from '@/components/audio-card';
import { useFavorites } from '@/lib/hooks/use-favorites';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import imageCompression from 'browser-image-compression';
import { AvatarCropDialog } from '@/components/profile/avatar-crop-dialog';
import { DeleteAccountSection } from '@/components/profile/delete-account-section';
import { OfflineDownloadsSection } from '@/components/profile/offline-downloads-section';
import { BlockedUsersSection } from '@/components/profile/blocked-users-section';
import { CertificatesSection } from '@/components/profile/certificates-section';

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

export default function ProfielPage() {
  const { profile, loadingProfile, refreshProfile } = useProfile();
  const navigate = useNavigate();
  const { favorites, toggleFavorite } = useFavorites();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [isLoadingFaqs, setIsLoadingFaqs] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const startEditingName = () => {
    setNameValue(profile?.naam || '');
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 0);
  };

  const saveName = async () => {
    if (!profile || nameValue.trim() === (profile.naam || '')) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ naam: nameValue.trim() })
        .eq('id', profile.id);
      if (error) throw error;
      refreshProfile();
    } catch (err) {
      console.error('Failed to update name:', err);
    } finally {
      setSavingName(false);
      setEditingName(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropFile(file);
    setShowCropDialog(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCroppedUpload = async (blob: Blob) => {
    if (!profile) return;
    setShowCropDialog(false);
    setUploadingAvatar(true);

    try {
      const compressed = await imageCompression(new File([blob], 'avatar.webp', { type: 'image/webp' }), {
        maxSizeMB: 0.2,
        maxWidthOrHeight: 400,
        useWebWorker: true,
      });

      const filePath = `${profile.id}/avatar.webp`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, compressed, { upsert: true, contentType: 'image/webp' });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const avatarUrl = `${publicUrl}?t=${Date.now()}`;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrl })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      refreshProfile();
    } catch (err) {
      console.error('Avatar upload failed:', err);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const mostRecentMonday = useMemo(() => getMostRecentMondayUTC(), []);

  const { contentPlays } = useContentPlays(
    profile
      ? { userId: profile.id, startTime: mostRecentMonday }
      : { disabled: true },
  );

  // Fetch FAQs from Supabase
  useEffect(() => {
    const fetchFaqs = async () => {
      setIsLoadingFaqs(true);
      try {
        const roleScope = profile?.role === 'hoofdhartuser' ? 'hoofdhartuser' : null;
        let query = supabase
          .from('faqs')
          .select('*')
          .order('id');

        if (roleScope) {
          query = query.eq('role_scope', roleScope);
        } else {
          query = query.is('role_scope', null);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching FAQs:', error);
          // Add default FAQs if there's an error fetching from the database
          setFaqs([
            {
              id: '1',
              question: 'Hoe vaak kan ik de app gebruiken?',
              answer:
                'Je kunt de app zo vaak gebruiken als je wilt. Er is geen limiet op het aantal sessies.',
            },
            {
              id: '2',
              question: 'Kan ik mijn abonnement opzeggen?',
              answer:
                'Ja, je kunt je abonnement op elk moment opzeggen via de instellingen in je profiel.',
            },
            {
              id: '3',
              question: 'Hoe werken de meditaties?',
              answer:
                "Onze meditaties zijn ontworpen om je te helpen ontspannen en mindfulness te beoefenen. Je kunt kiezen uit verschillende thema's en lengtes.",
            },
            {
              id: '4',
              question: 'Zijn er offline opties?',
              answer:
                'Momenteel moet je verbonden zijn met internet om de app te gebruiken. We werken aan offline functionaliteit voor toekomstige updates.',
            },
          ]);
          setIsLoadingFaqs(false);
          return;
        }

        if (data && data.length > 0) {
          setFaqs(data);
        } else {
          // Add default FAQs if none are found in the database
          setFaqs([
            {
              id: '1',
              question: 'Hoe vaak kan ik de app gebruiken?',
              answer:
                'Je kunt de app zo vaak gebruiken als je wilt. Er is geen limiet op het aantal sessies.',
            },
            {
              id: '2',
              question: 'Kan ik mijn abonnement opzeggen?',
              answer:
                'Ja, je kunt je abonnement op elk moment opzeggen via de instellingen in je profiel.',
            },
            {
              id: '3',
              question: 'Hoe werken de meditaties?',
              answer:
                "Onze meditaties zijn ontworpen om je te helpen ontspannen en mindfulness te beoefenen. Je kunt kiezen uit verschillende thema's en lengtes.",
            },
            {
              id: '4',
              question: 'Zijn er offline opties?',
              answer:
                'Momenteel moet je verbonden zijn met internet om de app te gebruiken. We werken aan offline functionaliteit voor toekomstige updates.',
            },
          ]);
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
        // Add default FAQs if there's an exception
        setFaqs([
          {
            id: '1',
            question: 'Hoe vaak kan ik de app gebruiken?',
            answer:
              'Je kunt de app zo vaak gebruiken als je wilt. Er is geen limiet op het aantal sessies.',
          },
          {
            id: '2',
            question: 'Kan ik mijn abonnement opzeggen?',
            answer:
              'Ja, je kunt je abonnement op elk moment opzeggen via de instellingen in je profiel.',
          },
          {
            id: '3',
            question: 'Hoe werken de meditaties?',
            answer:
              "Onze meditaties zijn ontworpen om je te helpen ontspannen en mindfulness te beoefenen. Je kunt kiezen uit verschillende thema's en lengtes.",
          },
          {
            id: '4',
            question: 'Zijn er offline opties?',
            answer:
              'Momenteel moet je verbonden zijn met internet om de app te gebruiken. We werken aan offline functionaliteit voor toekomstige updates.',
          },
        ]);
      } finally {
        setIsLoadingFaqs(false);
      }
    };

    fetchFaqs();
  }, []);

  return (
    <div className="min-h-screen p-8">
      <div className="flex flex-col items-center gap-8">
        <div className="relative group">
          <Avatar className="w-24 h-24">
            {profile?.avatar_url && <AvatarImage src={profile.avatar_url} />}
            <AvatarFallback className="bg-green-600 text-white text-3xl font-semibold">
              {(() => {
                const [firstName, lastName] = splitNames(profile?.naam || '');
                return (firstName?.charAt(0) || '') + (lastName?.charAt(0) || '');
              })() || 'U'}
            </AvatarFallback>
          </Avatar>
          <button
            type="button"
            className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingAvatar}
          >
            <Camera className="h-6 w-6 text-white" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileSelect}
          />
          {uploadingAvatar && (
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
              <div className="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <AvatarCropDialog
          file={cropFile}
          open={showCropDialog}
          onClose={() => setShowCropDialog(false)}
          onCrop={handleCroppedUpload}
        />
        <div className="text-center">
          {editingName ? (
            <div className="flex items-center justify-center gap-2 mb-1">
              <input
                ref={nameInputRef}
                type="text"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveName();
                  if (e.key === 'Escape') setEditingName(false);
                }}
                className="text-2xl font-semibold text-center bg-transparent border-b-2 border-primary outline-none w-48"
                disabled={savingName}
              />
              <button type="button" onClick={saveName} disabled={savingName} className="text-green-600 hover:text-green-700">
                <Check className="h-5 w-5" />
              </button>
              <button type="button" onClick={() => setEditingName(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <h2
              className="text-2xl font-semibold mb-1 flex items-center justify-center gap-2 group cursor-pointer"
              onClick={startEditingName}
            >
              {profile?.naam || 'Loading...'}
              <Pencil className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </h2>
          )}
          <p className="text-sm text-muted-foreground mb-2">
            {profile?.email || 'Loading...'}
          </p>
          <Button
            variant="ghost"
            className="text-red-500 hover:text-red-600 hover:bg-red-50 mt-2 mb-4"
            size="sm"
            onClick={async () => {
              await supabase.auth.signOut();
              window.location.href = LOGIN_PATH;
            }}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Uitloggen
          </Button>
        </div>

        <div className="w-full max-w-2xl space-y-8">
          <ActivityCalendar />

          <SectionBackground className="p-6 bg-transparent border-2 border-[#8BC34A] rounded-lg">
            <div className="space-y-4 w-full">
              <h3 className="text-lg font-medium">Totaal Beluisterd:</h3>
              <TypesBarChart contentPlays={contentPlays} />
            </div>
          </SectionBackground>

          {contentPlays.topPlayed.length > 0 && (
            <SectionBackground className="p-6 bg-transparent border-2 border-[#8BC34A] rounded-lg">
              <div className="space-y-4 w-full">
                <h3 className="text-lg font-medium">Top 3 meest beluisterd:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {contentPlays.topPlayed.slice(0, 3).map((topPlay) => (
                    <AudioCard
                      key={topPlay.content.id}
                      content={topPlay.content}
                      hideDescription={true}
                      onClick={() => {
                        navigate(
                          getDeepLink(
                            topPlay.content.content_type,
                            topPlay.content.id,
                          ),
                        );
                      }}
                    />
                  ))}
                </div>
              </div>
            </SectionBackground>
          )}

          <div className="mt-12 pt-6 border-t border-border">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/profiel/faq"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <span>Veelgestelde Vragen</span>
              </Link>
              <Link
                to="/profiel/contact"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <span>Contact</span>
              </Link>
              <Link
                to="/profiel/documenten"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <span>Documenten</span>
              </Link>
            </div>
          </div>

          <CertificatesSection />
          <BlockedUsersSection />
          <OfflineDownloadsSection />
          <DeleteAccountSection />
        </div>
      </div>
    </div>
  );
}
